import { InsightsFiltersOptionsQuery } from 'generated/resources/graphql'
import { isNil, orderBy, uniq } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { countries } from 'modules/core/lib/countryList'

const getNationalitiesToShow = (filtersOptionsData: InsightsFiltersOptionsQuery) => {
  const insightsFilterData = filtersOptionsData.reports.insightsFiltersConnection.nodes.find(
    (filterData) => filterData.filterType === 'nationalities'
  )

  if (insightsFilterData && insightsFilterData.values.length > 0) {
    return insightsFilterData.values
  }

  // Fallback to employees nationalities
  return uniq(
    filtersOptionsData.employees.employees
      .map((employee) => employee.nationality)
      .filter((nationality): nationality is string => !isNil(nationality))
  )
}

export const useNationalityOptions = (filtersOptionsData: InsightsFiltersOptionsQuery) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const options = useUnsafeMemo(() => {
    const countriesByCode = countries(locale).reduce<Record<string, string>>((acc, country) => {
      acc[country.value.toLowerCase()] = country.label

      return acc
    }, {})

    const currentNationalities = getNationalitiesToShow(filtersOptionsData).map((nationality) =>
      nationality.toLowerCase()
    )

    const unorderedOptions = currentNationalities.map((nationality) => ({
      label: countriesByCode[nationality] ?? nationality,
      value: nationality,
    }))

    return orderBy(unorderedOptions, (country) => country.label, 'asc')
  }, [filtersOptionsData])

  return options
}
